import React, { useState, forwardRef } from "react";
import { FaPlus } from "react-icons/fa";

interface ToggleButtonProps {
  isActive: boolean;
  activeText: string;
  inactiveText: string;
  activeIcon?: React.ReactNode;
  inactiveIcon?: React.ReactNode;
  activeColor?: string;
  inactiveColor?: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  disabledText?: string;
  disableAnimation?: boolean;
}

const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      isActive,
      activeText,
      inactiveText,
      activeIcon = <>🥳</>,
      inactiveIcon = <FaPlus className="w-3 h-3" />,
      activeColor = "bg-[var(--color-green)] text-white",
      inactiveColor = "",
      onClick,
      className = "",
      disabled = false,
      disableAnimation = false,
      disabledText,
    },
    ref
  ) => {
    const [bounce, setBounce] = useState(false);

    const handleClick = () => {
      if (disabled) return;

      if (!isActive && !disableAnimation) {
        setBounce(true);
        onClick();
        setTimeout(() => setBounce(false), 1500);
      } else {
        onClick();
      }
    };

    // Determine which text to display based on state
    const displayText = disabled
      ? disabledText || (isActive ? activeText : inactiveText)
      : isActive
      ? activeText
      : inactiveText;

    // Determine the icon based on state
    const displayIcon = disabled
      ? isActive
        ? activeIcon
        : inactiveIcon
      : isActive
      ? activeIcon
      : inactiveIcon;

    return (
      <button
        ref={ref}
        onClick={handleClick}
        disabled={disabled}
        className={`
          text-sm
          flex
          items-center
          gap-2
          px-2
          py-[2px]
          rounded-2xl
          border-solid
          border-gray-200
          ${isActive ? activeColor : inactiveColor}
          ${bounce ? "animate-bounce" : ""}
          ${disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
          ${className}
        `}
      >
        {displayIcon}
        <div className="text-nowrap">{displayText}</div>
      </button>
    );
  }
);

export default ToggleButton;
